import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

interface ParticlesProps {
  preset: string
}

const PARTICLE_PRESETS: Record<string, {
  count: number
  size: number
  color: string
  speed: number
  spread: number
  opacity: number
  movement: 'float' | 'fall' | 'rise' | 'burst'
}> = {
  fireflies: {
    count: 100,
    size: 0.08,
    color: '#FFF8DC',
    speed: 0.3,
    spread: 10,
    opacity: 0.8,
    movement: 'float'
  },
  
  rain: {
    count: 500,
    size: 0.02,
    color: '#A0C4FF',
    speed: 2.0,
    spread: 10,
    opacity: 0.6,
    movement: 'fall'
  },
  
  snow: {
    count: 300,
    size: 0.04,
    color: '#FFFFFF',
    speed: 0.5,
    spread: 10,
    opacity: 0.9,
    movement: 'fall'
  },
  
  stardust: {
    count: 50,
    size: 0.12,
    color: '#FFD700',
    speed: 0.2,
    spread: 8,
    opacity: 1.0,
    movement: 'float'
  },
  
  petals: {
    count: 80,
    size: 0.1,
    color: '#FFB7C5',
    speed: 0.4,
    spread: 10,
    opacity: 0.8,
    movement: 'fall'
  },
  
  bubbles: {
    count: 40,
    size: 0.15,
    color: '#E0F7FA',
    speed: 0.3,
    spread: 6,
    opacity: 0.7,
    movement: 'rise'
  },

  // ---- 数据库绘本粒子 preset（对齐 seeds 中 particle 取值）----
  leaves: {
    count: 120,
    size: 0.1,
    color: '#66BB6A',
    speed: 0.5,
    spread: 10,
    opacity: 0.8,
    movement: 'fall'
  },
  pollen: {
    count: 150,
    size: 0.06,
    color: '#FFF3B0',
    speed: 0.25,
    spread: 10,
    opacity: 0.7,
    movement: 'float'
  },
  starlight: {
    count: 90,
    size: 0.09,
    color: '#FFECB3',
    speed: 0.2,
    spread: 12,
    opacity: 0.9,
    movement: 'float'
  },
  dandelion_seeds: {
    count: 70,
    size: 0.12,
    color: '#F5F5F5',
    speed: 0.35,
    spread: 12,
    opacity: 0.75,
    movement: 'float'
  },
  dust_motes: {
    count: 60,
    size: 0.05,
    color: '#FFF9C4',
    speed: 0.15,
    spread: 8,
    opacity: 0.6,
    movement: 'float'
  },
  wind_leaves: {
    count: 110,
    size: 0.11,
    color: '#A5D6A7',
    speed: 0.8,
    spread: 14,
    opacity: 0.8,
    movement: 'fall'
  },
  magic_sparkles: {
    count: 100,
    size: 0.08,
    color: '#FFD54F',
    speed: 0.3,
    spread: 9,
    opacity: 0.9,
    movement: 'rise'
  },
  floating_hearts: {
    count: 50,
    size: 0.14,
    color: '#FF8A80',
    speed: 0.25,
    spread: 8,
    opacity: 0.85,
    movement: 'rise'
  },
  water_droplets: {
    count: 200,
    size: 0.03,
    color: '#81D4FA',
    speed: 1.5,
    spread: 10,
    opacity: 0.7,
    movement: 'fall'
  },
  fireflies_glow: {
    count: 90,
    size: 0.1,
    color: '#FFEE58',
    speed: 0.2,
    spread: 10,
    opacity: 0.9,
    movement: 'float'
  },
  color_sprinkles: {
    count: 180,
    size: 0.07,
    color: '#FFAB91',
    speed: 0.45,
    spread: 11,
    opacity: 0.85,
    movement: 'float'
  }
}

export default function StoryParticles({ preset }: ParticlesProps) {
  const meshRef = useRef<THREE.Points>(null)
  const config = PARTICLE_PRESETS[preset] || PARTICLE_PRESETS.fireflies
  
  const [positions, velocities] = useMemo(() => {
    const pos = new Float32Array(config.count * 3)
    const vel = new Float32Array(config.count * 3)
    
    for (let i = 0; i < config.count; i++) {
      const i3 = i * 3
      pos[i3] = (Math.random() - 0.5) * config.spread
      pos[i3 + 1] = (Math.random() - 0.5) * config.spread
      pos[i3 + 2] = (Math.random() - 0.5) * config.spread
      
      vel[i3] = (Math.random() - 0.5) * config.speed
      vel[i3 + 1] = config.movement === 'fall' 
        ? -config.speed * (0.5 + Math.random())
        : config.movement === 'rise'
          ? config.speed * (0.5 + Math.random())
          : (Math.random() - 0.5) * config.speed
      vel[i3 + 2] = (Math.random() - 0.5) * config.speed
    }
    
    return [pos, vel]
  }, [config])
  
  useFrame((_, delta) => {
    if (!meshRef.current) return
    
    const positionAttribute = meshRef.current.geometry.attributes.position
    const posArray = positionAttribute.array as Float32Array
    
    for (let i = 0; i < config.count; i++) {
      const i3 = i * 3
      
      // 更新位置
      posArray[i3] += velocities[i3] * delta
      posArray[i3 + 1] += velocities[i3 + 1] * delta
      posArray[i3 + 2] += velocities[i3 + 2] * delta
      
      // 边界重置
      if (posArray[i3 + 1] < -config.spread / 2 || posArray[i3 + 1] > config.spread / 2) {
        posArray[i3] = (Math.random() - 0.5) * config.spread
        posArray[i3 + 1] = config.movement === 'fall' 
          ? config.spread / 2 
          : config.movement === 'rise'
            ? -config.spread / 2
            : (Math.random() - 0.5) * config.spread
        posArray[i3 + 2] = (Math.random() - 0.5) * config.spread
      }
      
      if (Math.abs(posArray[i3]) > config.spread / 2) {
        posArray[i3] *= -0.9
      }
      if (Math.abs(posArray[i3 + 2]) > config.spread / 2) {
        posArray[i3 + 2] *= -0.9
      }
    }
    
    positionAttribute.needsUpdate = true
  })
  
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    return geo
  }, [positions])

  return (
    <points ref={meshRef} position={[0, 0, -2]}>
      <primitive object={geometry} attach="geometry" />
      <pointsMaterial
        size={config.size}
        color={config.color}
        transparent
        opacity={config.opacity}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  )
}
